import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { environment } from '../../../environments/environment';

const SYSTEM_ID_STORAGE_KEY = 'bsfms_system_id';
const SESSION_ID_STORAGE_KEY = 'bsfms_session_id';
const SESSION_BINDING_STORAGE_KEY = 'bsfms_session_binding';

/**
 * Auth endpoints whose 401 means "no session / wrong credentials", never
 * "token expired": they must not trigger a refresh, and AuthService handles
 * their outcome itself.
 */
const SESSION_ENDPOINTS = ['/auth/refresh', '/auth/end-session'];
const NO_REFRESH_ENDPOINTS = [...SESSION_ENDPOINTS, '/auth/login', '/auth/logout'];

/**
 * Generate an RFC4122 v4 UUID. Uses crypto.randomUUID() when available
 * (modern browsers + secure context) and falls back to crypto.getRandomValues
 * for older browsers / HTTP dev servers.
 */
function generateUuid(): string {
    const cryptoObj: any = (window as any).crypto || (window as any).msCrypto;
    if (cryptoObj?.randomUUID) {
        return cryptoObj.randomUUID();
    }
    if (cryptoObj?.getRandomValues) {
        const buf = new Uint8Array(16);
        cryptoObj.getRandomValues(buf);
        buf[6] = (buf[6] & 0x0f) | 0x40; // version 4
        buf[8] = (buf[8] & 0x3f) | 0x80; // variant 10
        const hex = Array.from(buf).map(b => b.toString(16).padStart(2, '0')).join('');
        return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    }
    // Last-resort fallback (non-crypto). Acceptable because this is an
    // identifier, not a secret.
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

/**
 * Get (or lazily create) a stable per-browser system identifier. Persisted
 * in localStorage so it survives across logins and tabs; same value is sent
 * on every request from this browser until the user clears site data.
 */
function getSystemId(): string {
    try {
        let id = localStorage.getItem(SYSTEM_ID_STORAGE_KEY);
        if (!id) {
            id = generateUuid();
            localStorage.setItem(SYSTEM_ID_STORAGE_KEY, id);
        }
        return id;
    } catch {
        return 'browser-unknown';
    }
}

/**
 * What identifies "this login" inside a token, without storing the token: the
 * server session id (`sid`), which survives token refreshes, or for tokens
 * without one (local mode) the user and issue time.
 */
function sessionBindingOf(token: string): string {
    try {
        const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
        return payload.sid || `${payload.userId}:${payload.iat}`;
    } catch {
        return 'unknown';
    }
}

/**
 * Get (or lazily create) a per-login session identifier. Bound to the
 * current login (see sessionBindingOf) so a new login → new session id, while
 * a refreshed token keeps it. Stored in sessionStorage so it dies with the
 * tab and never leaks across users on a shared machine.
 */
function getSessionId(currentToken: string | null): string {
    if (!currentToken) {
        return 'no-session';
    }
    try {
        const binding = sessionBindingOf(currentToken);
        const bound = sessionStorage.getItem(SESSION_BINDING_STORAGE_KEY);
        let id = sessionStorage.getItem(SESSION_ID_STORAGE_KEY);
        if (!id || bound !== binding) {
            id = generateUuid();
            sessionStorage.setItem(SESSION_ID_STORAGE_KEY, id);
            sessionStorage.setItem(SESSION_BINDING_STORAGE_KEY, binding);
        }
        return id;
    } catch {
        return 'session-unknown';
    }
}

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    constructor(
        private authService: AuthService,
        private router: Router
    ) {}

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // Get token from auth service
        const token = this.authService.getToken();
        const authReq = this.withAuthHeaders(req, token);

        // Handle the request and catch errors
        return next.handle(authReq).pipe(
            catchError((error: HttpErrorResponse) => {
                if (token && this.shouldRefresh(req, error, token)) {
                    return this.refreshAndRetry(req, next, token, error);
                }
                return this.handleError(req, error);
            })
        );
    }

    private withAuthHeaders(req: HttpRequest<any>, token: string | null): HttpRequest<any> {
        // Always stamp X-System-Id and X-Session-Id so the API's default
        // system fields (system_id, session_id) get real values instead of
        // "unknown". These are not auth-bearing — they're identifiers used
        // for audit trail and tracking.
        let headers = req.headers
            .set('X-System-Id', getSystemId())
            .set('X-Session-Id', getSessionId(token));

        if (token) {
            headers = headers.set('Authorization', `Bearer ${token}`);
        }

        return req.clone({ headers });
    }

    /**
     * A request sent with a token to our API failed because that token is no
     * longer accepted: 401, or 403 TOKEN_EXPIRED (the API keeps 403 for expired
     * tokens for older clients). change-password answers 401 for a wrong
     * current password, so only its 403 TOKEN_EXPIRED qualifies.
     */
    private shouldRefresh(req: HttpRequest<any>, error: HttpErrorResponse, sentToken: string | null): boolean {
        if (!sentToken || !req.url.startsWith(environment.apiUrl)) return false;
        if (NO_REFRESH_ENDPOINTS.some(p => req.url.includes(p)) || req.url.includes('/api/public/')) return false;
        if (error.status === 403) return error.error?.code === 'TOKEN_EXPIRED';
        return error.status === 401 && !req.url.includes('/change-password');
    }

    /**
     * Get a fresh token and replay the request once. Every request failing
     * meanwhile joins the same refresh (AuthService shares one in-flight call,
     * and a cross-tab lock serialises tabs), so only one refresh runs at a time.
     */
    private refreshAndRetry(
        req: HttpRequest<any>, next: HttpHandler, sentToken: string, originalError: HttpErrorResponse
    ): Observable<HttpEvent<any>> {
        // Another request already renewed the token since this one was sent.
        const current = this.authService.getToken();
        const token$ = current && current !== sentToken ? of(current) : this.authService.refreshAccessToken();

        return token$.pipe(
            catchError(refreshError => {
                if (AuthService.isAuthFailure(refreshError)) {
                    // The session is gone (idle, revoked, signed out elsewhere).
                    this.authService.logout();
                    this.router.navigate(['/auth/login']);
                }
                // Network/server trouble: keep the session, surface the original error.
                return throwError(() => originalError);
            }),
            switchMap(newToken => next.handle(this.withAuthHeaders(req, newToken)).pipe(
                catchError((retryError: HttpErrorResponse) => this.handleError(req, retryError))
            ))
        );
    }

    private handleError(req: HttpRequest<any>, error: HttpErrorResponse): Observable<never> {
        // Refresh / end-session failures are expected when there is no session;
        // AuthService deals with them (no redirect, e.g. on public pages).
        if (SESSION_ENDPOINTS.some(p => req.url.includes(p))) {
            return throwError(() => error);
        }

        // If 401 Unauthorized, logout and redirect to login
        // Exception: change-password endpoints return 401 for wrong current password,
        // not for an expired/invalid token — so don't logout in that case.
        if (error.status === 401 && !req.url.includes('/change-password') && !req.url.includes('/api/public/')) {
            this.authService.logout();
            this.router.navigate(['/auth/login']);
        }

        // If 403 Forbidden, show access denied message. ACCOUNT_UNAVAILABLE
        // is different: the account behind this token was deleted or
        // deactivated, so the session is dead — sign out rather than leave
        // the user on a page where every request fails.
        if (error.status === 403) {
            if (error.error?.code === 'ACCOUNT_UNAVAILABLE') {
                this.authService.logout();
                this.router.navigate(['/auth/login']);
            } else {
                console.error('Access denied. Insufficient permissions.');
            }
        }

        return throwError(() => error);
    }
}
